import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Info, TrendingUp, TrendingDown, BarChart3, PieChart } from 'lucide-react';
import MobileTabTooltip from '../ui/MobileTabTooltip';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import AllocationTab from '../AllocationTab';
import { useDashboardChartData } from './useDashboardChartData';
import HoldingsByCurrencyCard from './HoldingsByCurrencyCard';
import { INVESTMENT_TYPE_LABELS, CHART_COLORS, getTypeColor, formatCurrency } from './constants';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

function SamplePerformanceMetrics({ summary, investments }) {
  if (!summary || !investments.length) return null;

  const totalInvested = summary.total_invested || 0;
  const currentValue = summary.current_value || 0;
  const totalReturn = currentValue - totalInvested;
  const returnPct = totalInvested > 0 ? ((totalReturn / totalInvested) * 100) : 0;

  const activeInvestments = investments.filter(i => i.owner_type !== 'related');

  const bestPerformer = activeInvestments.reduce((best, inv) => {
    const gain = (inv.current_value || 0) - (inv.amount || 0);
    const pct = inv.amount > 0 ? (gain / inv.amount) * 100 : 0;
    return (!best || pct > best.pct) ? { ...inv, gain, pct } : best;
  }, null);

  const worstPerformer = activeInvestments.reduce((worst, inv) => {
    const gain = (inv.current_value || 0) - (inv.amount || 0);
    const pct = inv.amount > 0 ? (gain / inv.amount) * 100 : 0;
    return (!worst || pct < worst.pct) ? { ...inv, gain, pct } : worst;
  }, null);

  const formatPct = (v) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-2">Performance Metrics</h3>
        <p className="text-sm text-slate-500">Static return metrics based on invested vs current value</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Return</CardTitle>
            {totalReturn >= 0 ? <TrendingUp className="h-4 w-4 text-emerald-600" /> : <TrendingDown className="h-4 w-4 text-red-600" />}
          </CardHeader>
          <CardContent>
            <div data-testid="sample-total-return" className={`text-xl sm:text-2xl font-mono font-bold ${totalReturn >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-sensitive="monetary">
              {formatCurrency(totalReturn)}
            </div>
            <p className="text-xs text-slate-500 mt-1"><span data-sensitive="rate">{formatPct(returnPct)}</span> overall</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Total Invested</CardTitle>
            <BarChart3 className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-mono font-bold text-slate-900" data-sensitive="monetary">{formatCurrency(totalInvested)}</div>
            <p className="text-xs text-slate-500 mt-1">Cost basis</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Current Value</CardTitle>
            <BarChart3 className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-mono font-bold text-slate-900" data-sensitive="monetary">{formatCurrency(currentValue)}</div>
            <p className="text-xs text-slate-500 mt-1">Market value</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-slate-600">Investments</CardTitle>
            <PieChart className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl sm:text-2xl font-mono font-bold text-slate-900">{summary.total_investments}</div>
            <p className="text-xs text-slate-500 mt-1">Active investments</p>
          </CardContent>
        </Card>
      </div>

      {(bestPerformer || worstPerformer) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {bestPerformer && (
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-4">
                <p className="text-xs font-medium text-emerald-600 uppercase tracking-wide mb-1">Best Performer</p>
                <p className="font-semibold text-slate-900" data-sensitive="asset-name">{bestPerformer.name}</p>
                <p className="text-sm text-slate-500">{INVESTMENT_TYPE_LABELS[bestPerformer.type] || bestPerformer.type}</p>
                <p className="text-lg font-mono font-bold text-emerald-600 mt-1" data-sensitive="monetary">{formatPct(bestPerformer.pct)} ({formatCurrency(bestPerformer.gain)})</p>
              </CardContent>
            </Card>
          )}
          {worstPerformer && (
            <Card className={`${worstPerformer.pct >= 0 ? 'border-emerald-200 bg-emerald-50/50' : 'border-amber-200 bg-amber-50/50'}`}>
              <CardContent className="p-4">
                <p className={`text-xs font-medium uppercase tracking-wide mb-1 ${worstPerformer.pct >= 0 ? 'text-emerald-600' : 'text-amber-600'}`}>Lowest Performer</p>
                <p className="font-semibold text-slate-900" data-sensitive="asset-name">{worstPerformer.name}</p>
                <p className="text-sm text-slate-500">{INVESTMENT_TYPE_LABELS[worstPerformer.type] || worstPerformer.type}</p>
                <p className={`text-lg font-mono font-bold mt-1 ${worstPerformer.pct >= 0 ? 'text-emerald-600' : 'text-amber-600'}`} data-sensitive="monetary">{formatPct(worstPerformer.pct)} ({formatCurrency(worstPerformer.gain)})</p>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

const SamplePortfolioTab = () => {
  const [summary, setSummary] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [innerTab, setInnerTab] = useState('overview');

  const chartData = useDashboardChartData(summary, investments);

  useEffect(() => {
    loadSampleData();
  }, []);

  const loadSampleData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const headers = getAuthHeaders();
      const [summaryRes, investmentsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/portfolio/summary?include_demo=true`, { headers, cache: 'no-store' }),
        fetch(`${BACKEND_URL}/api/investments?include_demo=true`, { headers, cache: 'no-store' }),
      ]);
      if (!summaryRes.ok || !investmentsRes.ok) throw new Error('Failed to load sample data');
      setSummary(await summaryRes.json());
      setInvestments(await investmentsRes.json());
    } catch {
      setError('Failed to load sample portfolio data');
      toast.error('Failed to load sample portfolio');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse" data-testid="sample-portfolio-skeleton" aria-busy="true">
        {/* Header skeleton */}
        <div>
          <div className="h-7 w-56 bg-slate-200 rounded mb-2"></div>
          <div className="h-4 w-80 bg-slate-100 rounded"></div>
        </div>

        {/* Read-only notice skeleton */}
        <div className="h-12 bg-purple-50 border border-purple-100 rounded-lg"></div>

        {/* Hero card skeleton (mimics the big purple summary card) */}
        <div className="bg-linear-to-r from-purple-200 to-indigo-200 rounded-xl p-6 space-y-4">
          <div className="h-6 w-48 bg-white/60 rounded"></div>
          <div className="h-12 w-64 bg-white/70 rounded"></div>
          <div className="h-4 w-32 bg-white/60 rounded"></div>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/40">
            <div className="bg-white/40 rounded-lg p-3 space-y-2">
              <div className="h-3 w-12 bg-white/70 rounded"></div>
              <div className="h-6 w-24 bg-white/70 rounded"></div>
              <div className="h-3 w-20 bg-white/70 rounded"></div>
            </div>
            <div className="bg-white/40 rounded-lg p-3 space-y-2">
              <div className="h-3 w-14 bg-white/70 rounded"></div>
              <div className="h-6 w-24 bg-white/70 rounded"></div>
              <div className="h-3 w-20 bg-white/70 rounded"></div>
            </div>
          </div>
        </div>

        {/* Tabs strip skeleton */}
        <div className="flex gap-2">
          <div className="h-9 w-28 bg-purple-200 rounded"></div>
          <div className="h-9 w-24 bg-slate-200 rounded"></div>
          <div className="h-9 w-28 bg-slate-200 rounded"></div>
        </div>

        {/* Investments-by-type card grid skeleton */}
        <div>
          <div className="h-6 w-48 bg-slate-200 rounded mb-4"></div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="bg-slate-200 rounded-lg shadow-md h-28"></div>
            ))}
          </div>
        </div>

        <span className="sr-only">Loading sample portfolio…</span>
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">{error || 'No sample data available'}</p>
      </div>
    );
  }

  return (
    <div data-testid="sample-portfolio-content" className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h2 className="text-lg sm:text-2xl font-serif font-bold text-slate-900">Sample Portfolio</h2>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-4 h-4 text-slate-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs text-sm">
                <p>A pre-built portfolio with diversified investments. This data is read-only and separate from your real investments.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <p className="text-sm text-slate-500">Explore how MyInvestFolio presents a diversified investment portfolio</p>
      </div>

      {/* Read-only notice */}
      <div className="text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg p-3">
        This is a read-only sample portfolio shared across all users. It is completely separate from your real investments.
      </div>

      {/* Portfolio Summary Card */}
      <Card className="bg-linear-to-r from-purple-500 to-indigo-600 text-white">
        <CardHeader>
          <CardTitle className="text-xl sm:text-3xl font-serif font-bold">Sample Portfolio Value</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <div data-testid="sample-total-value" className="text-3xl sm:text-5xl font-mono font-bold mb-2" data-sensitive="monetary">
                {formatCurrency(summary.current_value)}
              </div>
              <p className="text-purple-100 text-sm sm:text-lg">
                Across {summary.total_investments} investment{summary.total_investments !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-4 border-t border-white/20">
              <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                <p className="text-xs text-purple-100 uppercase tracking-wide">Direct</p>
                <p data-testid="sample-direct-value" className="text-lg sm:text-2xl font-bold mt-1" data-sensitive="monetary">
                  {formatCurrency(summary?.direct_portfolio?.current_value || 0)}
                </p>
                <p className="text-xs text-purple-100 mt-1">{summary?.direct_portfolio?.count || 0} investments</p>
              </div>
              <div className="bg-white/10 rounded-lg p-2 sm:p-3">
                <p className="text-xs text-indigo-100 uppercase tracking-wide">Indirect</p>
                <p data-testid="sample-indirect-value" className="text-lg sm:text-2xl font-bold mt-1" data-sensitive="monetary">
                  {formatCurrency(summary?.indirect_portfolio?.current_value || 0)}
                </p>
                <p className="text-xs text-indigo-100 mt-1">{summary?.indirect_portfolio?.count || 0} via entities</p>
              </div>
            </div>
            <div className="bg-indigo-500/20 rounded-lg p-2 sm:p-3 border border-indigo-300/30">
              <p className="text-xs text-indigo-100 uppercase tracking-wide">Related Assets</p>
              <p className="text-base sm:text-lg font-semibold mt-1" data-sensitive="monetary">
                {formatCurrency(summary?.related_assets?.current_value || 0)}
              </p>
              <p className="text-xs text-indigo-100 mt-1">{summary?.related_assets?.count || 0} asset(s) - memorandum only</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Total Investments by Currency — matches Overview tab layout */}
      <HoldingsByCurrencyCard investments={investments} />

      {/* Inner tabs: Investments / Allocation / Performance */}
      <Tabs value={innerTab} onValueChange={setInnerTab} className="space-y-6">
        <TabsList className="inline-flex bg-slate-100 w-full sm:w-auto overflow-x-auto scrollbar-none p-1 gap-0.5">
          <TabsTrigger data-testid="sample-investments-subtab" value="overview" className="data-[state=active]:bg-purple-600! data-[state=active]:text-white! font-semibold text-xs sm:text-sm whitespace-nowrap" title="View all sample investments across different asset types">Investments</TabsTrigger>
          <TabsTrigger data-testid="sample-allocation-subtab" value="allocation" className="data-[state=active]:bg-purple-600! data-[state=active]:text-white! font-semibold text-xs sm:text-sm whitespace-nowrap" title="See how the sample portfolio is distributed across categories">Allocation</TabsTrigger>
          <TabsTrigger data-testid="sample-performance-subtab" value="performance" className="data-[state=active]:bg-purple-600! data-[state=active]:text-white! font-semibold text-xs sm:text-sm whitespace-nowrap" title="Review returns and top/bottom performers in the sample portfolio">Performance</TabsTrigger>
          <MobileTabTooltip />
        </TabsList>

        {/* Investments Sub-tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Investments by Type Cards */}
          {chartData.completeTypeChartData.filter(i => i.count > 0).length > 0 && (
            <div>
              <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-4 sm:mb-6">Investments by Type</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                {chartData.completeTypeChartData.filter(i => i.count > 0).map((item, index) => (
                  <Card key={item.type} className={`${getTypeColor(index)} text-white shadow-md hover:shadow-lg transition-shadow`}>
                    <CardHeader className="p-3 sm:pb-3">
                      <CardTitle className="text-xs sm:text-base font-medium capitalize leading-tight">
                        {INVESTMENT_TYPE_LABELS[item.type] || item.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-3 pt-0">
                      <div className="text-lg sm:text-2xl font-mono font-bold mb-1" data-sensitive="monetary">{formatCurrency(item.value)}</div>
                      <p className="text-white/90 text-xs sm:text-sm">{item.count} investment{item.count !== 1 ? 's' : ''}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Indirect Investments by Entity */}
          {summary?.indirect_portfolio?.entities?.length > 0 && (
            <div>
              <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-4">Indirect Investments by Entity</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {summary.indirect_portfolio.entities.map((entity) => (
                  <Card key={entity.entity_id} className="hover:shadow-lg transition-shadow">
                    <CardHeader className="p-3 sm:pb-3">
                      <CardTitle className="text-base sm:text-lg font-semibold text-slate-900" data-sensitive="identifier">{entity.entity_name}</CardTitle>
                      <Badge className="bg-blue-500 text-white w-fit">{entity.entity_type}</Badge>
                    </CardHeader>
                    <CardContent className="p-3">
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm text-slate-600">Country</p>
                          <p className="font-medium" data-sensitive="identifier">{entity.entity_country}</p>
                        </div>
                        <div className="border-t pt-2">
                          <p className="text-xl sm:text-2xl font-bold text-blue-600" data-sensitive="monetary">{formatCurrency(entity.total_value)}</p>
                          <p className="text-sm text-slate-500 mt-1">{entity.investment_count} investment{entity.investment_count !== 1 ? 's' : ''}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Full Investment List */}
          <div>
            <h3 className="text-lg sm:text-2xl font-serif font-bold text-slate-900 mb-4">All Sample Investments</h3>
            <div className="grid gap-3">
              {investments.map((inv) => (
                <Card key={inv.investment_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate" data-sensitive="asset-name">{inv.name}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{INVESTMENT_TYPE_LABELS[inv.type] || inv.type}</Badge>
                        <Badge variant="outline" className={`text-xs ${inv.owner_type === 'direct' ? 'border-emerald-300 text-emerald-700' : inv.owner_type === 'indirect' ? 'border-blue-300 text-blue-700' : 'border-purple-300 text-purple-700'}`}>
                          {inv.owner_type}
                        </Badge>
                        {inv.institution && <span className="text-xs text-slate-500" data-sensitive="identifier">{inv.institution}</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-lg font-bold text-slate-900" data-sensitive="monetary">{formatCurrency(inv.current_value || inv.amount)}</p>
                      <p className="text-xs text-slate-500" data-sensitive="identifier">{inv.currency}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Allocation Sub-tab */}
        <TabsContent value="allocation" className="space-y-8">
          <AllocationTab
            portfolioSummary={summary}
            ownershipChartData={chartData.ownershipChartData}
            typeChartData={chartData.typeChartData}
            liquidityChartData={chartData.liquidityChartData}
            countryChartData={chartData.countryChartData}
            durationChartData={chartData.durationChartData}
            counterpartyChartData={chartData.counterpartyChartData}
            topCounterparty={chartData.topCounterparty}
            topThirdPartyCounterparty={chartData.topThirdPartyCounterparty}
            topConcentrationPct={chartData.topConcentrationPct}
            isConcentrated={chartData.isConcentrated}
            unspecifiedPct={chartData.unspecifiedPct}
            chartColors={CHART_COLORS}
            formatCurrency={formatCurrency}
          />
        </TabsContent>

        {/* Performance Sub-tab */}
        <TabsContent value="performance" className="space-y-6">
          <SamplePerformanceMetrics summary={summary} investments={investments} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SamplePortfolioTab;
